import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Wrench, Loader2, Plus, Trash2, Save } from "lucide-react";

export default function Manutenzione() {
  const [man, setMan] = useState({ modalita: "annuo", prezzo_annuo: 0, prezzo_kwp: 0, attiva_default: false, descrizione: "", attivita: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/settings/manutenzione").then((r) => setMan(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  const setManAtt = (i, k, v) => setMan((m) => ({ ...m, attivita: m.attivita.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const setManTr = (k, v) => setMan((m) => ({ ...m, trasferta: { ...(m.trasferta || {}), [k]: v } }));
  const setManTier = (i, k, v) => setMan((m) => ({ ...m, listino: (m.listino || []).map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        descrizione: (man.descrizione || "").trim(),
        modalita: man.modalita || "annuo",
        prezzo_annuo: 0,
        prezzo_kwp: 0,
        attiva_default: !!man.attiva_default,
        use_listino: man.use_listino !== false,
        listino: (man.listino || []).map((t, i) => ({ min_kwp: Number(t.min_kwp) || 0, max_kwp: (t.max_kwp === "" || t.max_kwp === null || t.max_kwp === undefined) ? null : Number(t.max_kwp), prezzo_annuo: Number(t.prezzo_annuo) || 0, attivo: t.attivo !== false, order: i + 1, modalita: t.modalita || "auto" })),
        trasferta: {
          enabled: man.trasferta?.enabled !== false,
          distanza_inclusa_km: Number(man.trasferta?.distanza_inclusa_km) || 0,
          costo_per_km: Number(man.trasferta?.costo_per_km) || 0,
          andata_ritorno: man.trasferta?.andata_ritorno !== false,
          costo_min: Number(man.trasferta?.costo_min) || 0,
          costo_max: (man.trasferta?.costo_max === "" || man.trasferta?.costo_max === null || man.trasferta?.costo_max === undefined) ? null : Number(man.trasferta?.costo_max),
          arrotonda_km: Number(man.trasferta?.arrotonda_km) || 0,
          arrotonda_importo: Number(man.trasferta?.arrotonda_importo) || 0,
          franchigia: Number(man.trasferta?.franchigia) || 0,
          use_hq_fallback: man.trasferta?.use_hq_fallback !== false,
        },
        attivita: (man.attivita || []).filter((a) => (a.label || "").trim()).map((a, i) => ({ key: a.key || "", label: a.label.trim(), order: i + 1, active: a.active !== false })),
      };
      const { data } = await api.put("/settings/manutenzione", body);
      setMan(data);
      toast.success("Manutenzione salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="manutenzione-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2"><Wrench className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-xl font-heading font-bold">Manutenzione</h1>
            <p className="text-xs text-muted-foreground">Voce economica opzionale Manutenzione programmata</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-manutenzione-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      </header>

      <div className="p-4 lg:p-8 max-w-4xl">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mb-4">Configura descrizione, modalità e attività disponibili. <b>Il prezzo si imposta esclusivamente in fase di preventivazione</b> (nessun prezzo di default).</p>
            <Card className="rounded-lg border-border p-5 max-w-2xl space-y-4">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!man.attiva_default} onCheckedChange={(v) => setMan((m) => ({ ...m, attiva_default: v }))} data-testid="man-attiva-default" /> Attiva di default nei nuovi preventivi</label>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</Label>
                <Textarea value={man.descrizione} onChange={(e) => setMan((m) => ({ ...m, descrizione: e.target.value }))} rows={2} data-testid="man-descrizione" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Modalità prezzo (default)</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={man.modalita} onChange={(e) => setMan((m) => ({ ...m, modalita: e.target.value }))} data-testid="man-cfg-modalita">
                    <option value="annuo">€ / anno</option>
                    <option value="kwp">€ / kWp</option>
                  </select>
                </div>
              </div>
              <div className="rounded border border-border p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={man.use_listino !== false} onCheckedChange={(v) => setMan((m) => ({ ...m, use_listino: v }))} data-testid="man-use-listino" /> Listino automatico per fascia di potenza</label>
                {man.use_listino !== false && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_1fr_auto_auto] gap-2 text-[10px] uppercase tracking-widest text-muted-foreground px-1"><span>Min kWp</span><span>Max kWp</span><span>€/anno</span><span>Su prev.</span><span></span></div>
                    {(man.listino || []).map((t, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto_auto] gap-2 items-center" data-testid={`man-tier-${i}`}>
                        <Input type="number" value={t.min_kwp ?? ""} onChange={(e) => setManTier(i, "min_kwp", e.target.value)} />
                        <Input type="number" value={t.max_kwp ?? ""} placeholder="∞" onChange={(e) => setManTier(i, "max_kwp", e.target.value)} />
                        <Input type="number" value={t.prezzo_annuo ?? ""} disabled={t.modalita === "preventivo"} onChange={(e) => setManTier(i, "prezzo_annuo", e.target.value)} />
                        <label className="flex justify-center px-2"><input type="checkbox" checked={t.modalita === "preventivo"} onChange={(e) => setManTier(i, "modalita", e.target.checked ? "preventivo" : "auto")} data-testid={`man-tier-prev-${i}`} /></label>
                        <Button size="icon" variant="ghost" onClick={() => setMan((m) => ({ ...m, listino: m.listino.filter((_, idx) => idx !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => setMan((m) => ({ ...m, listino: [...(m.listino || []), { min_kwp: 0, max_kwp: null, prezzo_annuo: 0, attivo: true, modalita: "auto" }] }))} data-testid="man-tier-add"><Plus className="h-4 w-4 mr-1.5" /> Aggiungi fascia</Button>
                  </div>
                )}
              </div>
              <div className="rounded border border-border p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={man.trasferta?.enabled !== false} onCheckedChange={(v) => setManTr("enabled", v)} data-testid="man-tr-enabled" /> Costo di trasferta (oltre la distanza inclusa)</label>
                {man.trasferta?.enabled !== false && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Distanza inclusa (km)</Label><Input type="number" value={man.trasferta?.distanza_inclusa_km ?? ""} onChange={(e) => setManTr("distanza_inclusa_km", e.target.value)} data-testid="man-tr-inclusa" /></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Costo per km (€)</Label><Input type="number" step="0.05" value={man.trasferta?.costo_per_km ?? ""} onChange={(e) => setManTr("costo_per_km", e.target.value)} data-testid="man-tr-km" /></div>
                    <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><Switch checked={man.trasferta?.andata_ritorno !== false} onCheckedChange={(v) => setManTr("andata_ritorno", v)} /> Andata + ritorno</label></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Costo min (€)</Label><Input type="number" value={man.trasferta?.costo_min ?? ""} onChange={(e) => setManTr("costo_min", e.target.value)} /></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Costo max (€)</Label><Input type="number" placeholder="nessuno" value={man.trasferta?.costo_max ?? ""} onChange={(e) => setManTr("costo_max", e.target.value)} /></div>
                    <div className="flex items-end"><label className="flex items-center gap-2 text-sm"><Switch checked={man.trasferta?.use_hq_fallback !== false} onCheckedChange={(v) => setManTr("use_hq_fallback", v)} /> Fallback sede TORRI</label></div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Attività disponibili</Label>
                <div className="space-y-2 mt-1">
                  {(man.attivita || []).map((a, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`man-cfg-att-${i}`}>
                      <Switch checked={a.active !== false} onCheckedChange={(v) => setManAtt(i, "active", v)} />
                      <Input value={a.label} onChange={(e) => setManAtt(i, "label", e.target.value)} placeholder="Descrizione attività" />
                      <Button size="icon" variant="ghost" onClick={() => setMan((m) => ({ ...m, attivita: m.attivita.filter((_, idx) => idx !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setMan((m) => ({ ...m, attivita: [...(m.attivita || []), { key: "", label: "", active: true }] }))} data-testid="man-cfg-add"><Plus className="h-4 w-4 mr-1.5" /> Aggiungi attività</Button>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
